/* eslint-disable no-console */
/* eslint-disable i18next/no-literal-string */
/* eslint-disable no-underscore-dangle */
import { EventType, identifyUser as identifyAnalyticsUser, trackEvent as trackAnalyticsEvent } from '@cmctechnology/webinvest-analytics/dist/js/analytics';
import { AccountTypeInput, PaymentMethod } from '../models/application';
import { TrackEventNames, ApplicationFormPaths } from '../constants/analyticsConstants';
import { IApplicationFormPath } from '../models/analyticsTrackEventAttributes';
import { logError } from '../logger/logger';
import { Theme } from '../models/analyticsTheme';
import { initializeAnalyticsPackage } from '../hooks/useAnalyticsInit';

declare global {
  interface Window {
    fireWhenMparticleReady?: (callback: () => void) => Promise<void>;
    mParticle?: any;
    // Native bridges
    webkit?: { messageHandlers?: { native?: { postMessage: (message: any) => void } } };
    Android?: { onEvent?: (message: string) => void };
  }
}

const isNativeBridgeAvailable = (): boolean => Boolean(window.webkit?.messageHandlers?.native || window.Android?.onEvent);

const postToNative = (payload: any): void => {
  if (window.webkit?.messageHandlers?.native) {
    window.webkit.messageHandlers.native.postMessage(payload);
    return;
  }
  if (window.Android?.onEvent) {
    window.Android.onEvent(JSON.stringify(payload));
  }
};

const getApplicationSubmittedEventNames = (accountType?: AccountTypeInput) => {
  const eventNames = [TrackEventNames.ApplicationSubmitted];

  if (accountType === AccountTypeInput.Individual) eventNames.push(TrackEventNames.ApplicationSubmittedIndividual);

  return eventNames;
};

const getNativeApplicationSubmittedEventName = () => TrackEventNames.ApplicationSubmitted;

const isBigScriptReady = () => typeof window.fireWhenMparticleReady === 'function' && Boolean(window.mParticle);

const getBigScriptInstance = () => {
  const instances = window.mParticle?._instances ?? {};
  const instanceNames = Object.keys(instances);
  const instanceName = Object.prototype.hasOwnProperty.call(instances, 'default_instance') ? 'default_instance' : instanceNames.pop();

  return instanceName ? window.mParticle?.getInstance(instanceName) : undefined;
};

const trackEventWithBigScript = async (eventName: string, eventType: any, data: any) => {
  await window.fireWhenMparticleReady!(() => {
    getBigScriptInstance()?.logEvent(eventName, eventType, { ...data });
  });
};

const trackEventWithAnalyticsPackage = async (eventName: string, eventType: any, data: any) => {
  try {
    await initializeAnalyticsPackage();
    await trackAnalyticsEvent(eventName, data, { type: eventType });
  } catch (error) {
    logError(error);
  }
};

export const trackEvent = async (eventName: string, eventType: any, data: any) => {
  if (isBigScriptReady()) {
    await Promise.all([trackEventWithBigScript(eventName, eventType, data), trackEventWithAnalyticsPackage(eventName, eventType, data)]);
    return;
  }

  await trackEventWithAnalyticsPackage(eventName, eventType, data);
};

const identifyUserWithBigScript = async (crc: string, email: string, userAttributes: any) => {
  await window.fireWhenMparticleReady!(() => {
    const currentUser = getBigScriptInstance()?.Identity?.getCurrentUser();

    if (currentUser?.isLoggedIn() && currentUser?.getUserIdentities()?.userIdentities?.customerid === crc) {
      return;
    }

    if (currentUser?.isLoggedIn()) {
      window.mParticle?.endSession();
    }

    window.mParticle?.Identity?.login({
      userIdentities: {
        customerid: crc
      },
      onUserAlias: (alias: any) => alias.setUserAttributes({ ...userAttributes, Email: email })
    });
  });
};

const identifyUserWithAnalyticsPackage = async (crc: string, email: string, userAttributes: any) => {
  await initializeAnalyticsPackage();
  await identifyAnalyticsUser(crc, { ...userAttributes, Email: email });
};

export const identifyUser = async (crc: string, email: string, userAttributes: any) => {
  try {
    if (isBigScriptReady()) {
      await Promise.all([identifyUserWithBigScript(crc, email, userAttributes), identifyUserWithAnalyticsPackage(crc, email, userAttributes)]);
      return;
    }

    await identifyUserWithAnalyticsPackage(crc, email, userAttributes);
  } catch (error) {
    logError(error);
  }
};

export const trackApplication = async (
  status: ApplicationFormPaths,
  accountType?: AccountTypeInput,
  method?: PaymentMethod,
  employment?: string,
  idType?: string
) => {
  const attributes: IApplicationFormPath = {
    Url: window.location.href,
    ApplicationStatus: status,
    Theme: Theme.Cmc
  };

  // mparticle doesn't like undefined propeties so only create them if they exist
  if (accountType) attributes.AccountType = accountType;
  if (method) attributes.PaymentMethod = method;
  if (employment) attributes.Employment = employment;
  if (idType) attributes.Identification = idType;

  try {
    await trackEvent(TrackEventNames.ApplicationForm, EventType.Navigation, attributes);
  } catch (error) {
    logError(error);
  }
};

export const trackApplicationSubmitted = async (
  status: ApplicationFormPaths,
  accountType?: AccountTypeInput,
  account_id?: string,
  person_id?: string,
  email_address?: string
) => {
  const eventNames = getApplicationSubmittedEventNames(accountType);
  const data: IApplicationFormPath = {
    Url: window.location.href,
    ApplicationStatus: status,
    Theme: Theme.Cmc
  };

  // mparticle doesn't like undefined propeties so only create them if they exist
  if (accountType) data.AccountType = accountType;
  if (account_id) data.AccountId = account_id;
  if (person_id) data.PersonId = person_id;
  if (email_address) data.EmailAddress = email_address;

  try {
    // If inside a native webview, forward to native and suppress mParticle to avoid duplicates
    if (isNativeBridgeAvailable()) {
      const eventName = getNativeApplicationSubmittedEventName();
      postToNative({
        eventName,
        data: { ...data, EventName: eventName },
        attrs: {}
      });
      return;
    }

    await Promise.all(eventNames.map((eventName) => trackEvent(eventName, EventType.Navigation, { ...data, EventName: eventName })));
  } catch (error) {
    logError(error);
  }
};
